import React, { useState } from 'react';
import {
  FiMoreVertical,
  FiTrash2,
  FiClock,
  FiZap,
  FiPlus,
  FiCopy,
  FiTerminal,
  FiVideo,
  FiCode,
} from 'react-icons/fi';
import { CommandStep } from '../../../../../types/flow';
import { Button } from '../ui/FlowButton';
import { Input } from '../ui/FlowInput';
import { cn } from '../lib/utils';

interface CommandTimelineProps {
  commands: CommandStep[];
  onReorder: (commands: CommandStep[]) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onUpdateSleep: (id: string, duration: number) => void;
  onAddSleep: (duration: number) => void;
  onAddCustomCommand: (command: string, label?: string) => void;
  onUpdateCommand?: (id: string, patch: Partial<CommandStep>) => void;
  isBaseFlow?: boolean;
}

export const CommandTimeline: React.FC<CommandTimelineProps> = ({
  commands,
  onReorder,
  onDelete,
  onDuplicate,
  onUpdateSleep,
  onAddSleep,
  onAddCustomCommand,
  onUpdateCommand,
  isBaseFlow = false,
}) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [showSleepInput, setShowSleepInput] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [sleepDuration, setSleepDuration] = useState(isBaseFlow ? '1' : '2.0');
  const [customCommand, setCustomCommand] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [editingSleepId, setEditingSleepId] = useState<string | null>(null);

  const handleDragStart = (id: string) => setDraggedId(id);

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) return;

    const draggedIndex = commands.findIndex(c => c.id === draggedId);
    const targetIndex = commands.findIndex(c => c.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newCommands = [...commands];
    const [removed] = newCommands.splice(draggedIndex, 1);
    newCommands.splice(targetIndex, 0, removed);
    onReorder(newCommands);
  };

  const handleDragEnd = () => setDraggedId(null);

  const handleAddSleepClick = () => {
    const duration = parseFloat(sleepDuration);
    if (!isNaN(duration) && duration > 0) {
      onAddSleep(duration);
      setShowSleepInput(false);
      setSleepDuration('2.0');
    }
  };

  const handleAddCustomCommandClick = () => {
    if (customCommand.trim()) {
      onAddCustomCommand(customCommand.trim(), customLabel.trim() || undefined);
      setShowCustomInput(false);
      setCustomCommand('');
      setCustomLabel('');
    }
  };

  return (
    <div className="adslot-timeline-wrapper">
      <div className="adslot-timeline-header">
        <h3 className="adslot-timeline-title">
          {isBaseFlow ? 'Base Command' : 'Command Timeline'}
        </h3>
        <div className="adslot-timeline-header-actions">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setShowCustomInput(!showCustomInput);
              setShowSleepInput(false);
            }}
          >
            <FiTerminal className="adslot-icon-xs adslot-icon-inline" />
            Custom
          </Button>
          {!isBaseFlow && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowSleepInput(!showSleepInput);
                setShowCustomInput(false);
              }}
            >
              <FiClock className="adslot-icon-xs adslot-icon-inline" />
              Add Sleep
            </Button>
          )}
        </div>
      </div>

      {showSleepInput && (
        <div className="adslot-timeline-add-block">
          <FiClock className="adslot-icon-xs adslot-timeline-icon" />
          <span className="adslot-timeline-label">Sleep (seconds):</span>
          <Input
            type="number"
            step="0.1"
            min="0.1"
            value={sleepDuration}
            onChange={e => setSleepDuration(e.target.value)}
            className="adslot-timeline-number-input"
          />
          <Button size="sm" onClick={handleAddSleepClick}>
            <FiPlus className="adslot-icon-xs adslot-icon-inline" />
            Add
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowSleepInput(false)}
          >
            Cancel
          </Button>
        </div>
      )}

      {showCustomInput && (
        <div className="adslot-timeline-add-block adslot-timeline-add-block-column">
          <div className="adslot-timeline-add-header">
            <FiTerminal className="adslot-icon-xs adslot-timeline-icon" />
            <span className="adslot-timeline-label-strong">
              Add Custom Command
            </span>
          </div>
          <div className="adslot-timeline-row">
            <Input
              type="text"
              placeholder="Label (optional, e.g. Custom Action)"
              value={customLabel}
              onChange={e => setCustomLabel(e.target.value)}
            />
          </div>
          <div className="adslot-timeline-row">
            <Input
              type="text"
              placeholder="Luna Command"
              value={customCommand}
              onChange={e => setCustomCommand(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleAddCustomCommandClick();
              }}
            />
            <Button
              size="sm"
              onClick={handleAddCustomCommandClick}
              disabled={!customCommand.trim()}
            >
              <FiPlus className="adslot-icon-xs adslot-icon-inline" />
              Add
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowCustomInput(false);
                setCustomCommand('');
                setCustomLabel('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="adslot-timeline-list">
        {commands.length === 0 ? (
          <div className="adslot-timeline-empty">
            <FiZap className="adslot-timeline-empty-icon" />
            <p className="adslot-timeline-empty-title">No commands yet</p>
            <p className="adslot-timeline-empty-subtitle">
              Use the remote on the left to build your navigation steps
            </p>
          </div>
        ) : (
          commands.map((cmd, index) => (
            <div
              key={cmd.id}
              draggable
              onDragStart={() => handleDragStart(cmd.id)}
              onDragOver={e => handleDragOver(e, cmd.id)}
              onDragEnd={handleDragEnd}
              className={cn(
                'adslot-timeline-item',
                draggedId === cmd.id && 'adslot-timeline-item-dragging',
              )}
            >
              <div className="adslot-timeline-grip">
                <FiMoreVertical className="adslot-icon-xs" />
              </div>

              <div className="adslot-timeline-index">{index + 1}</div>

              <div
                className={cn(
                  'adslot-timeline-type-icon',
                  cmd.type === 'sleep'
                    ? 'adslot-timeline-type-sleep'
                    : 'adslot-timeline-type-command',
                )}
              >
                {cmd.type === 'sleep' ? (
                  <FiClock className="adslot-icon-xs" />
                ) : cmd.type === 'video' ? (
                  <FiVideo className="adslot-icon-xs" />
                ) : cmd.type === 'mock-command' ? (
                  <FiCode className="adslot-icon-xs" />
                ) : (
                  <FiZap className="adslot-icon-xs" />
                )}
              </div>

              <div className="adslot-timeline-content">
                {cmd.type === 'command' ? (
                  <>
                    <p className="adslot-timeline-command-label">{cmd.label}</p>
                    <p className="adslot-timeline-command-meta">
                      {cmd.command}
                    </p>
                    {isBaseFlow && onUpdateCommand && (
                      <div className="adslot-base-command-fields">
                        <label className="adslot-base-command-field">
                          <span>Alias</span>
                          <Input
                            type="text"
                            value={cmd.label || ''}
                            onChange={e =>
                              onUpdateCommand(cmd.id, { label: e.target.value })
                            }
                          />
                        </label>
                        <label className="adslot-base-command-field">
                          <span>Sleep</span>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={cmd.sleep ?? ''}
                            onChange={e => {
                              const value = parseFloat(e.target.value);
                              onUpdateCommand(cmd.id, {
                                sleep: Number.isNaN(value) ? undefined : value,
                              });
                            }}
                          />
                        </label>
                        <label className="adslot-base-command-field">
                          <span>Timeout</span>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={cmd.timeout ?? ''}
                            onChange={e => {
                              const value = parseFloat(e.target.value);
                              onUpdateCommand(cmd.id, {
                                timeout: Number.isNaN(value) ? undefined : value,
                              });
                            }}
                          />
                        </label>
                      </div>
                    )}
                  </>
                ) : cmd.type === 'event' ? (
                  <>
                    <p className="adslot-timeline-command-label">
                      Event: {cmd.event || cmd.label}
                    </p>
                    <p className="adslot-timeline-command-meta">
                      {cmd.event}
                    </p>
                  </>
                ) : cmd.type === 'video' ? (
                  <>
                    <p className="adslot-timeline-command-label">
                      Video: {cmd.options?.type || 'action'}
                    </p>
                    <p className="adslot-timeline-command-meta">
                      {JSON.stringify(cmd.options || {})}
                    </p>
                  </>
                ) : cmd.type === 'mock-command' ? (
                  <>
                    <p className="adslot-timeline-command-label">
                      Mock: {cmd.substitute || cmd.label}
                    </p>
                    <p className="adslot-timeline-command-meta">
                      {cmd.substitute}
                    </p>
                  </>
                ) : (
                  <div className="adslot-timeline-sleep-edit">
                    {editingSleepId === cmd.id ? (
                      <Input
                        type="number"
                        step="0.1"
                        min="0.1"
                        defaultValue={cmd.duration}
                        onBlur={e => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val) && val > 0) {
                            onUpdateSleep(cmd.id, val);
                          }
                          setEditingSleepId(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            const val = parseFloat(
                              (e.target as HTMLInputElement).value,
                            );
                            if (!isNaN(val) && val > 0) {
                              onUpdateSleep(cmd.id, val);
                            }
                            setEditingSleepId(null);
                          }
                        }}
                        className="adslot-timeline-number-input"
                        autoFocus
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingSleepId(cmd.id)}
                        className="adslot-timeline-sleep-button"
                      >
                        Sleep {cmd.duration}s
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="adslot-timeline-actions">
                <button
                  type="button"
                  onClick={() => onDuplicate(cmd.id)}
                  className="adslot-timeline-icon-button"
                  title="Duplicate"
                  disabled={isBaseFlow}
                >
                  <FiCopy className="adslot-icon-xs" />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(cmd.id)}
                  className="adslot-timeline-icon-button adslot-timeline-icon-button-danger"
                  title="Delete"
                >
                  <FiTrash2 className="adslot-icon-xs" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
